import { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Skeleton,
  Typography,
  IconButton,
} from "@mui/material";
import useGetAreaMembers from "src/Pages/Company/OrganizationStructure/api/useGetAreaMembers";
import useOrgchartStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Utils/store";
import __ from "src/utils/Translations";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { Avatar, PresenceIndicator } from "@benefit-systems/hr-tools-ui";
import useIsAhr from "src/utils/hooks/useIsAhr";

const Skeletons = ({ count }: { count: number }) =>
  Array.from(new Array(count)).map((key) => (
    <Skeleton
      variant="rectangular"
      height={66}
      sx={{ margin: "24px 0" }}
      key={key}
    />
  ));

const AreaMembersDialog = ({
  companyId,
  structureId,
}: {
  companyId: string;
  structureId: string;
}) => {
  const { isDialogOpen, areaDataForDialog, setIsDialogOpen } =
    useOrgchartStore();
  const { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, data } =
    useGetAreaMembers({ areaId: areaDataForDialog.id, companyId, structureId });
  const isAhr = useIsAhr();
  const { ref, inView } = useInView();
  const handleClose = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (hasNextPage && inView && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, inView, fetchNextPage, isLoading, hasNextPage]);

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleClose}
      scroll="body"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "15px",
          padding: 0,
        },
        "& .MuiDialogTitle-root": { padding: "24px 24px 0 24px" },
        "& .MuiDialogContent-root": { padding: 0 },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "60px",
          }}
        >
          <Typography variant="h6" fontWeight="fontWeightMedium">{`${__(
            "Członkowie"
          )} ${areaDataForDialog.name}`}</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ padding: 0 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="black"
                fillOpacity="0.56"
              />
            </svg>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ margin: "24px 0 0 0" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "8px",
            padding: "0 24px 24px 24px",
          }}
        >
          {data?.pages.map((areaMembersChunk) =>
            areaMembersChunk.content.map(
              ({
                presenceInformation,
                avatar,
                position,
                name,
                initials,
                id,
                fk,
              }) => (
                <Link
                  to={
                    isAhr
                      ? `/ahr/employees/${id}`
                      : `/company/edit/${companyId}/employee-management/employees/${id}`
                  }
                  style={{ textDecoration: "none" }}
                >
                  <Box
                    key={`MemberContainer_${id}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "24px",
                      borderRadius: "15px",
                      border: "1px solid white",
                      boxShadow: 0,
                      transition: "border .3s ease-in-out",
                      ":hover": {
                        cursor: "pointer",
                        border: "1px solid #2196F3",
                      },
                    }}
                  >
                    <PresenceIndicator status={presenceInformation?.status}>
                      <Avatar src={avatar} size={13}>
                        {initials}
                      </Avatar>
                    </PresenceIndicator>
                    <Box>
                      <Typography
                        variant="h6"
                        color="textPrimary"
                        sx={{
                          textDecoration: "none",
                        }}
                      >{`${name}`}</Typography>
                      {fk && (
                        <Typography variant="body2" color="textSecondary">
                          {`(FK ${fk})`}
                        </Typography>
                      )}
                      {position?.label && (
                        <Typography variant="body2" color="textSecondary">
                          {__(position.label)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Link>
              )
            )
          )}
          {isFetchingNextPage && <Skeletons count={5} />}
          <Box ref={ref} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AreaMembersDialog;
